import { ConsultationDexieDb } from "../db/ConsultationDexieDb";
import { PayloadStore } from "./PayloadStore";

export class IdbPayloadStore implements PayloadStore {
  constructor(private readonly db: ConsultationDexieDb) {}

  private blobToArrayBuffer(blob: Blob): Promise<ArrayBuffer> {
    const input = blob as Blob & { arrayBuffer?: () => Promise<ArrayBuffer> };

    if (typeof input.arrayBuffer === "function") {
      return input.arrayBuffer();
    }

    return new Promise<ArrayBuffer>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        if (reader.result instanceof ArrayBuffer) {
          resolve(reader.result);
          return;
        }

        reject(new Error("Unable to read stored audio payload."));
      };
      reader.onerror = () => {
        reject(reader.error ?? new Error("Unable to read stored audio payload."));
      };
      reader.readAsArrayBuffer(blob);
    });
  }

  private toArrayBuffer(bytes: Uint8Array): ArrayBuffer {
    const copy = new Uint8Array(bytes.byteLength);
    copy.set(bytes);
    return copy.buffer;
  }

  async putSegment(
    payloadKey: string,
    sessionId: string,
    segmentId: number,
    bytes: Uint8Array,
    checksum32?: number
  ): Promise<void> {
    await this.db.payloads.put({
      payloadKey,
      sessionId,
      segmentId,
      byteLength: bytes.byteLength,
      checksum32,
      payload: new Blob([this.toArrayBuffer(bytes)], {
        type: "application/octet-stream",
      }),
      createdAt: Date.now(),
    });
  }

  async readSegment(payloadKey: string): Promise<ArrayBuffer> {
    const record = await this.db.payloads.get(payloadKey);

    if (!record) {
      throw new Error(`Missing audio payload: ${payloadKey}`);
    }

    return this.blobToArrayBuffer(record.payload);
  }

  async deleteSegment(payloadKey: string): Promise<void> {
    await this.db.payloads.delete(payloadKey);
  }
}
